'use client';
import { useMemo } from 'react';
import { maybeHTMLorMarkdown } from '../shared/utils';
import { TableMobile } from './components/TableMobile';
import { TableDesktop } from './components/TableDesktop';
import { Check, Close, Download } from '@gjensidige/builders-icons';
import { Button, Text, Title, Flex } from '@gjensidige/builders-components';
import type { ProductInsuranceProps } from '../shared/types';
import type { ProductGroupTableProps } from './types';
import styles from './product-group-table.module.less';
import classNames from 'classnames';

export const ProductGroupTable = ({
  title = '',
  preface = '',
  link,
  group,
  downloads
}: ProductGroupTableProps) => {
  const products = group?.products || [];
  const features = useMemo(() => getAllFeaturesSorted(products), []);
  const teaser = !!(title || preface) && (
    <div className={styles.header}>
      <Title size="4" text={title} />
      <Text
        as="div"
        className={styles.htmlArea}
        dangerouslySetInnerHTML={{ __html: preface }}
        size="small"
      />
    </div>
  );

  return (
    <Flex
      center
      gap="none"
      fill
      className={classNames(styles.marginYParts, styles.wrapper)}
    >
      <TableDesktop features={features} products={products}>
        {teaser}
      </TableDesktop>
      <TableMobile features={features} products={products}>
        {teaser}
      </TableMobile>
      <Flex
        align="center"
        className={styles.footer}
        gap="sm xl"
        justify="center"
        wrap="wrap"
      >
        {!!downloads?.length && (
          <Flex gap="xs" direction="column">
            {downloads?.map(
              ({ linkText, linkTarget, download }, index: number) => (
                <div>
                  <Button
                    variant="action"
                    download={download}
                    href={linkTarget}
                    icon={<Download />}
                    key={index}
                    size="sm"
                  >
                    {linkText}
                  </Button>
                </div>
              )
            )}
          </Flex>
        )}
        {!!link && (
          <Button
            variant="primary"
            href={link.linkTarget}
            tracking={!link.trackingLabel || { eventLabel: link.trackingLabel }}
          >
            {link.linkText}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

function getAllFeaturesSorted(products: ProductInsuranceProps[]) {
  const all = new Map(); // Use Map to keep order, but also quickly detect duplicates
  products
    .slice() // Make clone to prevent sorting original array
    .sort((a, b) => (b.features?.length || 0) - (a.features?.length || 0)) // Sort by most features
    .forEach(
      ({ features }) =>
        features?.map((feature) => {
          const safeName = feature.name.replace(/[^a-z0-9]/g, '-');
          const uniqueId = `product-group-table-feature-${safeName}`;
          const uvp = maybeHTMLorMarkdown(feature.uvp);

          all.set(feature.name, { ...feature, uniqueId, uvp });
        })
    );
  return Array.from(all.values()); // Convert Map to Array
}

export function getCoverage(
  featureName: string,
  product: ProductInsuranceProps
) {
  const isCovered = product.features?.find(
    (feature) => feature.name === featureName
  );

  return {
    isCovered,
    className: isCovered ? styles.isCovered : '',
    text: isCovered ? isCovered.conditionText : '',
    icon: (
      <span
        aria-checked={!!isCovered}
        className={styles.coverageIcon}
        role="checkbox"
      >
        {isCovered ? <Check /> : <Close />}
      </span>
    )
  };
}
